import React, { useContext, useState } from 'react'
import BigModal from '../../../../components/Modals/BigModal'
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Divider,
} from '@mui/material'
import API from '../../../../services/API'
import { useNavigate } from 'react-router-dom'
import { GlobalContext } from '../../../../App'

const DeleteRecord = ({ open, onClose, recordID }) => {
  const [deleteType, setDeleteType] = useState('SOFT_DELETE')
  const [reason, setReason] = useState('')
  const [confirm, setConfirm] = useState(false)
  const navigate = useNavigate()
  const { setToast } = useContext(GlobalContext)

  const handleDelete = () => {
    if (!confirm || !reason.trim()) return

    API.post('/delete-epcr/' + recordID, {
      reason,
      action: deleteType,
    }).then((res) => {
      if (res.status === 200) {
        navigate('/patient-report-forms')
        setToast({ message: 'Record Deleted' })
        onClose()
      }
    })
  }

  return (
    <BigModal open={open} onClose={onClose} style={{ border: 'solid 2px red' }}>
      <Box display="flex" flexDirection="column" gap={3} padding={2}>
        <Typography variant="title2">
          Delete Electronic Patient Care Record
        </Typography>
        <Typography variant="body1">
          Please confirm how you wish to delete this electronic patient care
          record (ePCR).
        </Typography>

        <RadioGroup
          value={deleteType}
          onChange={(e) => setDeleteType(e.target.value)}
        >
          <FormControlLabel
            value="SOFT_DELETE"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="subtitle1">
                  <strong>Soft Delete</strong> (Recommended)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Hides the record from normal views. It remains securely stored
                  and can be retrieved via written request to AmbuNet.
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            value="HARD_DELETE"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="subtitle1">
                  <strong>Hard Delete</strong> (Permanent)
                </Typography>
                <Typography variant="body2" color="error">
                  Irreversibly deletes the record from the database. This action
                  cannot be undone and may affect legal or regulatory
                  obligations.
                </Typography>
              </Box>
            }
          />
        </RadioGroup>

        <TextField
          label="Reason for deletion"
          placeholder="e.g., Entered in error, duplicate entry, GDPR erasure request"
          multiline
          rows={3}
          fullWidth
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={confirm}
              onChange={(e) => setConfirm(e.target.checked)}
            />
          }
          label="I confirm this deletion is compliant with applicable guidance and legal obligations."
        />

        <Divider />

        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            ⚖️ <strong>Legal Notice</strong>
            <br />
            Under the{' '}
            <strong>UK General Data Protection Regulation (UK GDPR)</strong> and
            guidance from the <strong>Care Quality Commission (CQC)</strong>,
            you are responsible for the lawful handling and retention of patient
            records.
          </Typography>

          <Typography variant="body2" color="text.secondary">
            AmbuNet acts as the <strong>Data Processor</strong>. You, as the
            tenant organisation, are the <strong>Data Controller</strong>.
            Deleting an ePCR should only be done when legally and clinically
            appropriate. All deletions are logged for audit purposes.
          </Typography>
        </Box>

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            color={deleteType === 'HARD_DELETE' ? 'error' : 'primary'}
            disabled={!confirm || !reason.trim()}
            onClick={handleDelete}
          >
            {deleteType === 'HARD_DELETE'
              ? 'Confirm Hard Delete'
              : 'Soft Delete'}
          </Button>
        </Box>
      </Box>
    </BigModal>
  )
}

export default DeleteRecord
